import { useMemo, useState } from 'react';
import styles from './PriceSchemeBandsRulesTab.module.scss';
import { Prompt, Table, Typography } from '@weareopenr/component-library';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { usePriceBands } from 'src/hooks/usePriceBands';
import { useDeletePriceBand } from 'src/hooks/useDeletePriceBand';
import { useRules } from 'src/hooks/useRules/useRules';
import { PriceBandRuleModal } from '../PriceBandRuleModal';
import { PromptActions } from 'src/constants';
import { PriceBandRuleSteps } from '../PriceBandRuleModal/PriceBandRuleModalContent';
import { TPriceBandAndRules } from './types';
import { getPriceBandTableColumms } from './Table';

type TEditPriceBand = {
  priceBand: TPriceBandAndRules | null;
  step: number | null;
};

const DEFAULT_EDITING_STATE = {
  priceBand: null,
  step: null,
};

const PriceSchemeBandsRulesTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const [searchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue);

  const [editingPriceBand, setEditingPriceBand] = useState<TEditPriceBand>(DEFAULT_EDITING_STATE);
  const [deletingPriceBand, setDeletingPriceBand] = useState<TPriceBandAndRules>(null);

  const { data: priceRules, isLoading: isRulesLoading } = useRules(priceSchemeId);
  const { priceBands = [], isLoading: isPriceBandsLoading } = usePriceBands(priceSchemeId);
  const isLoadingData = isRulesLoading || isPriceBandsLoading;

  const resetEditingPriceBand = () => setEditingPriceBand(DEFAULT_EDITING_STATE);
  const resetDeletingPriceBand = () => setDeletingPriceBand(null);

  const { mutate: deletePriceBandMutation, isLoading: isDeleting } = useDeletePriceBand(resetDeletingPriceBand);

  const priceBandDeletePromptOpen = deletingPriceBand !== null;

  const removePromptTitle = `Are you sure you want to delete "${deletingPriceBand?.name}" price band?`;
  const removeVenuePromptSubTitle = 'This action will delete the price band and all associated rules.';

  const priceBandsAndRules: TPriceBandAndRules[] = useMemo(
    () =>
      priceBands.map((priceBand) => {
        const matchedRule = priceRules?.find((rule) =>
          rule.price_band ? rule.price_band.id === priceBand.priceBandId : false,
        );
        const priceBandAndRule = {
          ...priceBand,
          ...matchedRule,
        };
        return priceBandAndRule;
      }),
    [priceBands, priceRules],
  );

  const handleDelete = () => {
    if (deletingPriceBand !== null) {
      deletePriceBandMutation({ priceBand: deletingPriceBand, priceSchemeId });
    }
  };

  const handleCreateClick = () => setEditingPriceBand({ priceBand: null, step: PriceBandRuleSteps.Name });

  const handleEditClick = (priceBand: TPriceBandAndRules, step: PriceBandRuleSteps) =>
    setEditingPriceBand({ priceBand, step });
  const handleDeleteClick = (priceBand: TPriceBandAndRules) => setDeletingPriceBand(priceBand);

  const columns = getPriceBandTableColumms({
    setModalOpen: handleCreateClick,
    handleEdit: handleEditClick,
    handleDelete: handleDeleteClick,
  });

  if (isLoadingData) {
    return <SkeletonContainer hiddenTitle />;
  }

  return (
    <div className={styles['tab-container']}>
      <section className={styles['table-container']}>
        <Table
          className={styles['table-fill-height']}
          data={priceBandsAndRules}
          columns={columns}
          columnHeadersToRightAlign={['venuesCount', 'calculationValue', 'roundingValue', 'actions']}
          EmptyState={() => (
            <div className={styles['no-results']}>
              <Typography>No search results found for&nbsp;</Typography>
              <Typography weight="semi-bold">{`"${debouncedSearchValue}"`}</Typography>
            </div>
          )}
        />
      </section>
      <Prompt
        promptHeading={removePromptTitle}
        closePrompt={resetDeletingPriceBand}
        open={priceBandDeletePromptOpen}
        buttonHandler={handleDelete}
        buttonText="Delete"
        backButtonText={PromptActions.CANCEL}
        backButton={true}
        size="small"
        isSubmitting={isDeleting}
      >
        {removeVenuePromptSubTitle}
      </Prompt>
      <PriceBandRuleModal
        priceBandToEdit={editingPriceBand.priceBand}
        priceSchemeId={priceSchemeId}
        open={editingPriceBand.step !== null}
        step={editingPriceBand.step}
        closeModal={resetEditingPriceBand}
        priceBands={priceBands}
      />
    </div>
  );
};

export default PriceSchemeBandsRulesTab;
